import React from 'react';

/**
 * A panel's shared-state hook. Deliberately the *only* extension point
 * beyond `Content`/`Rail` — there is no generic "Provider" slot here, so
 * this can't grow into ErrorBoundary/Suspense/Permissions/FeatureFlags/
 * Telemetry/Localization over time. Those stay owned by `SidebarContainer`
 * itself, generic across every panel, never per-panel.
 */
export type SidebarUseSharedState = (props: any) => unknown;

export interface SidebarRegistration {
  readonly Content: React.ComponentType<any>;
  readonly Rail?: React.ComponentType<any>;
  readonly useSharedState?: SidebarUseSharedState;
  /**
   * When true the generic SidebarContainer header buttons (close, dock, expand,
   * fullscreen) are suppressed. Use this for panels that render their own
   * WorkspaceHeader with equivalent controls.
   */
  readonly hideContainerControls?: boolean;
}

export class SidebarRegistry {
  private static instance: SidebarRegistry;
  private registry: Map<string, SidebarRegistration> = new Map();

  private constructor() {}

  public static getInstance(): SidebarRegistry {
    if (!SidebarRegistry.instance) {
      SidebarRegistry.instance = new SidebarRegistry();
    }
    return SidebarRegistry.instance;
  }

  public register(id: string, registration: SidebarRegistration): void {
    this.registry.set(id, registration);
  }

  public get(id: string): React.ComponentType<any> | undefined {
    return this.registry.get(id)?.Content;
  }

  public getRegistration(id: string): SidebarRegistration | undefined {
    return this.registry.get(id);
  }

  public has(id: string): boolean {
    return this.registry.has(id);
  }
}

export const sidebarRegistry = SidebarRegistry.getInstance();
